import { useState } from "react";
import { Check, ChevronDown, Loader2, PenLine, Search, Sparkles, Square } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/shared/lib/utils";
import type { PhaseName } from "./types";

interface StatusBannerProps {
  phase: PhaseName | null;
  phaseLabel: string | null;
  elapsedMs: number;
  lastFrameTs: number | null;
  /** Set the first time the agent calls a tool. Accepted for call-site
   *  compatibility; no longer used (the stall watchdog was removed). */
  lastToolUsedTs: number | null;
  streaming: boolean;
  /** The user's current question. Accepted for call-site compatibility; no
   *  longer woven into the header (stage names only, 2026-07-30 feedback). */
  question?: string | null;
  /** Abort the in-flight stream. Renders the Stop control in this top bar
   *  (there is no docked composer while streaming). */
  onStop?: () => void;
}

/**
 * Streaming status line. The header message is ALWAYS the active stage name —
 * exactly the same string the expandable steps rail marks active — so the
 * collapsed and expanded views never disagree (2026-07-30 feedback: no witty
 * taglines, no stall interpolation, no timing in the message text). The
 * elapsed counter on the right is the only timing signal.
 */
const PHASE_LABELS: Record<PhaseName, string> = {
  thinking: "Thinking",
  querying: "Querying the intelligence layer",
  building: "Writing the answer",
  complete: "Done",
};

const STEP_ORDER: PhaseName[] = ["thinking", "querying", "building", "complete"];

// Shared tempo for the two "thinking" lights — the brand ring's traveling
// segment (animate-spin) and the action-label shimmer sweep (animate-shimmer)
// both run at this exact duration so they read as one synchronized light.
// One knob: lower it for a livelier ring, raise it for a calmer reveal. Tuned
// down per 2026-05-31 review — the ring read as too slow at the prior value.
const THINKING_LIGHT_TEMPO_MS = 5000;

function phaseIcon(phase: PhaseName | null) {
  const cls = "h-4 w-4";
  switch (phase) {
    case "thinking":
      return <Sparkles className={cls} />;
    case "querying":
      return <Search className={cls} />;
    case "building":
      return <PenLine className={cls} />;
    case "complete":
      return <Check className={cls} />;
    default:
      return <Loader2 className={cn(cls, "animate-spin")} />;
  }
}

function formatSecs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

export function StatusBanner({
  phase,
  elapsedMs,
  streaming,
  onStop,
}: StatusBannerProps) {
  const reduceMotion = useReducedMotion();
  const [showSteps, setShowSteps] = useState(false);

  const complete = phase === "complete" || !streaming;

  // Header message = active stage name, always. Identical to the string the
  // steps rail marks active, whether the rail is expanded or collapsed.
  const shownLabel = complete ? PHASE_LABELS.complete : PHASE_LABELS[phase ?? "thinking"];

  if (phase === null && !streaming) return null;

  const live = streaming && !complete;
  const currentStepIdx = phase ? STEP_ORDER.indexOf(phase) : -1;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "rounded-md border text-sm",
        complete
          ? "border-border bg-card text-muted-foreground"
          : "border-primary/20 bg-primary/[0.04] text-foreground",
      )}
    >
      <div className="flex items-center gap-2.5 px-3 py-2">
        {/* Brand ring as the live "thinking" indicator (mirrors the empty-state
            greeting logo). A bright primary segment travels clockwise around a
            faded ring while streaming. Honors prefers-reduced-motion. On
            complete, swaps to the phase icon (check). */}
        {live ? (
          <span
            aria-hidden
            style={reduceMotion ? undefined : { animationDuration: `${THINKING_LIGHT_TEMPO_MS}ms` }}
            className={cn(
              "inline-block h-5 w-5 shrink-0 rounded-full border-[3px] border-primary/25",
              reduceMotion ? "border-t-primary" : "border-t-primary animate-spin",
            )}
          />
        ) : (
          <span className="shrink-0 text-primary">{phaseIcon(phase)}</span>
        )}
        <span
          style={live && !reduceMotion ? { animationDuration: `${THINKING_LIGHT_TEMPO_MS}ms` } : undefined}
          className={cn(
            "min-w-0 flex-1 truncate font-medium",
            // Shimmer sweep on the live label (reduced-motion → plain text).
            // Base stops are an EXPLICIT foreground color — not currentColor,
            // which resolves against the text-transparent fill and made the
            // label invisible for most of each cycle. The primary band
            // (47–53% plateau) sweeps across as a highlight; the label stays
            // legible at every point of the cycle. Sweep duration is shared
            // with the brand ring so both lights move as one.
            live && !reduceMotion &&
              "animate-shimmer bg-[linear-gradient(90deg,hsl(var(--foreground))_0%,hsl(var(--foreground))_35%,hsl(var(--primary))_47%,hsl(var(--primary))_53%,hsl(var(--foreground))_65%,hsl(var(--foreground))_100%)] [background-size:200%_100%] bg-clip-text text-transparent",
          )}
        >
          {shownLabel}
        </span>
        {streaming ? (
          <span className="tabular-nums text-xs text-muted-foreground">
            {formatSecs(elapsedMs)}
          </span>
        ) : null}
        {streaming && onStop ? (
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop generating"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30 transition-colors hover:bg-primary/25"
          >
            <Square className="h-3 w-3 fill-current" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setShowSteps((s) => !s)}
          aria-expanded={showSteps}
          aria-label={showSteps ? "Hide steps" : "Show steps"}
          className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform duration-200", showSteps && "rotate-180")}
          />
        </button>
      </div>

      {showSteps ? (
        <ol className="flex flex-col gap-1 border-t border-border/60 px-3 py-2">
          {STEP_ORDER.map((step, i) => {
            const done = currentStepIdx > i || complete;
            const active = !complete && currentStepIdx === i;
            return (
              <li
                key={step}
                className={cn(
                  "flex items-center gap-2 text-xs",
                  done ? "text-muted-foreground" : active ? "text-foreground" : "text-muted-foreground/50",
                )}
              >
                <span className="shrink-0">
                  {done ? (
                    <Check className="h-3 w-3" />
                  ) : active ? (
                    <Loader2 className={cn("h-3 w-3", !reduceMotion && "animate-spin")} />
                  ) : (
                    <span className="block h-3 w-3 rounded-full border border-current" />
                  )}
                </span>
                {PHASE_LABELS[step]}
              </li>
            );
          })}
        </ol>
      ) : null}
    </div>
  );
}
